// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Opt-in for showing a live distance-to-green badge in the hole header.
//
// Deliberately a LOCAL, per-device setting like shot tracking: it only affects what this
// screen renders, so there is nothing to sync to a profile. Default is off.

import { create } from 'zustand';

const STORAGE_KEY = 'goffy:showDistance';

type AsyncStorageLike = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
};

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

let storageOverride: AsyncStorageLike | null = null;

const readStored = async (): Promise<string | null> => {
  if (storageOverride) return storageOverride.getItem(STORAGE_KEY);
  const local = getLocalStorage();
  if (local) return local.getItem(STORAGE_KEY);
  if (asyncStorage) return asyncStorage.getItem(STORAGE_KEY);
  return null;
};

const writeStored = async (value: string): Promise<void> => {
  if (storageOverride) return storageOverride.setItem(STORAGE_KEY, value);
  const local = getLocalStorage();
  if (local) {
    local.setItem(STORAGE_KEY, value);
    return;
  }
  if (asyncStorage) await asyncStorage.setItem(STORAGE_KEY, value);
};

type ShowDistanceState = {
  enabled: boolean;
  /** True once storage has been read, or once the flag was set explicitly. */
  hydrated: boolean;
  setEnabled: (enabled: boolean) => void;
};

export const useShowDistanceState = create<ShowDistanceState>((set) => ({
  enabled: false,
  hydrated: false,
  setEnabled: (enabled) => {
    set({ enabled, hydrated: true });
    void writeStored(enabled ? 'on' : 'off').catch(() => {
      // Losing the mirror costs one re-toggle on the next cold start.
    });
  },
}));

export const hydrateShowDistance = async (): Promise<void> => {
  try {
    const stored = await readStored();
    if (stored === null) {
      useShowDistanceState.setState({ hydrated: true });
      return;
    }
    if (useShowDistanceState.getState().hydrated) return;
    useShowDistanceState.setState({ enabled: stored === 'on', hydrated: true });
  } catch {
    useShowDistanceState.setState({ hydrated: true });
  }
};

// Hydrates on import; before that the flag reads as off, which fails safe —
// the badge can only ever appear late, never unasked.
void hydrateShowDistance();

export const useShowDistanceEnabled = (): boolean => useShowDistanceState((state) => state.enabled);

export const __setShowDistanceStorageForTests = (storage: AsyncStorageLike | null) => {
  storageOverride = storage;
};

export const __resetShowDistanceStateForTests = () => {
  useShowDistanceState.setState({ enabled: false, hydrated: false });
};
